import { useRef, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Users, AlertCircle, Sparkles } from "lucide-react";
import { useReveal } from "@/lib/animations";
import { PRODUCTS, BUSINESS_STRATEGIES } from "@/lib/data";
import { GoogleRating } from "@/components/GoogleRating";
import { Aurora } from "@/components/Aurora";
import { IULMotionGraphic } from "@/components/IULMotionGraphic";
import ProductFunnel from "@/pages/ProductFunnel";
import { KeyNumbers } from "@/components/funnel/KeyNumbers";
import {
  FIAMotionGraphic,
  TermMotionGraphic,
  FEXMotionGraphic,
  IBMotionGraphic,
  MortgageMotionGraphic,
} from "@/components/ProductMotionGraphics";

const MOTION_GRAPHICS = {
  iul: IULMotionGraphic,
  fia: FIAMotionGraphic,
  term: TermMotionGraphic,
  fex: FEXMotionGraphic,
  "infinite-banking": IBMotionGraphic,
  "mortgage-protection": MortgageMotionGraphic,
};

export default function ProductDetail({ source = "consumer" }) {
  const { slug } = useParams();
  const list = source === "business" ? BUSINESS_STRATEGIES : PRODUCTS;
  const product = list.find((p) => p.id === slug);
  const ref = useRef(null);
  useReveal(ref);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return <Navigate to={source === "business" ? "/business" : "/products"} replace />;
  }

  if (source === "consumer" && product.funnel) {
    return <ProductFunnel product={product} MotionGraphic={MOTION_GRAPHICS[product.id]} />;
  }

  const Icon = product.icon;
  const backTo = source === "business" ? "/business" : "/products";
  const backLabel = source === "business" ? "All business strategies" : "All products";
  const article = /^[AEIOU]/i.test(product.tag) ? "an" : "a";
  const isNavy = product.accent === "navy";
  const heroIconBg = isNavy ? "bg-navy-deep-gradient shadow-[#0B132B]/35" : "bg-breeze-gradient shadow-[#29ABE2]/25";

  return (
    <main ref={ref} data-testid={`product-detail-${product.id}`} className="bg-[#F9F6F0]">
      {/* Hero */}
      <section className="relative bg-[#F0EBE0] grain overflow-hidden">
        <Aurora className="z-0" intensity="subtle" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-36 md:pt-44 pb-20">
          <Link
            to={backTo}
            data-testid="product-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#334155] hover:text-[#29ABE2] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> {backLabel}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className={`w-16 h-16 rounded-2xl ${heroIconBg} text-white flex items-center justify-center shadow-lg mb-8`}>
                <Icon size={28} />
              </div>
              <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
                {product.tag} {source === "business" ? "· For business owners" : ""}
              </p>
              <h1
                data-reveal
                className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter leading-[0.92] text-[#0B132B]"
              >
                {product.name}
              </h1>
              <p data-reveal className="mt-7 text-lg text-[#334155] font-light leading-relaxed max-w-2xl">
                {product.summary || product.detail}
              </p>
              <div data-reveal className="mt-9 flex flex-wrap gap-4">
                <Link
                  to={`/quote?product=${encodeURIComponent(product.name)}`}
                  data-testid="product-detail-quote-button"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B132B] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-[#29ABE2] hover:scale-105"
                >
                  Get {article} {product.tag} quote <ArrowRight size={17} />
                </Link>
                <Link
                  to={backTo}
                  className="inline-flex items-center gap-2 rounded-full bg-white text-[#0B132B] font-semibold px-8 py-4 border border-[#0B132B]/10 transition-all duration-300 hover:border-[#29ABE2] hover:text-[#29ABE2]"
                >
                  See related strategies
                </Link>
              </div>
              <div data-reveal className="mt-8">
                <GoogleRating />
              </div>
            </div>

            {/* Right column — motion graphic for consumer products, poster card fallback */}
            {(() => {
              const MG = source === "consumer" ? MOTION_GRAPHICS[product.id] : null;
              return MG ? (
                <div data-reveal className="lg:col-span-5 relative lg:sticky lg:top-28">
                  <MG />
                </div>
              ) : (
            <div data-reveal className="lg:col-span-5 relative lg:sticky lg:top-28">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-breeze-gradient opacity-30 blur-3xl" aria-hidden="true" />
              <div className="relative rounded-[2rem] overflow-hidden bg-[#0B132B] text-white shadow-2xl shadow-[#0B132B]/30 border border-white/10">
                <div className={`relative ${isNavy ? "bg-navy-deep-gradient" : "bg-breeze-gradient"} p-8 md:p-10`}>
                  <Aurora intensity="intense" />
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-bold">
                        <Sparkles size={13} /> {product.tag}
                      </span>
                      <span className="font-heading font-black text-5xl sm:text-6xl leading-none tracking-tighter text-white/15">
                        {product.tag}
                      </span>
                    </div>
                    <div className="mt-10 flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur ring-1 ring-white/20 flex items-center justify-center animate-pulse-glow">
                        <Icon size={28} />
                      </div>
                      <div>
                        <p className="text-xs tracking-[0.2em] uppercase text-white/70 font-semibold">
                          At a glance
                        </p>
                        <p className="font-heading font-black text-2xl tracking-tight mt-0.5">
                          {product.name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-8 md:p-10 space-y-4">
                  {(product.benefits || product.points).slice(0, 4).map((b, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check size={18} className="text-[#29ABE2] mt-0.5 shrink-0" />
                      <span className="text-sm text-white/85 font-light leading-relaxed">{b}</span>
                    </div>
                  ))}
                  <div className="pt-4 mt-4 border-t border-white/10 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-white/55 font-semibold">
                        Trusted partners
                      </p>
                      <p className="font-heading font-bold text-lg mt-0.5">50+ A-rated carriers</p>
                    </div>
                    <Link
                      to={`/quote?product=${encodeURIComponent(product.name)}`}
                      className="w-11 h-11 rounded-full bg-[#29ABE2] hover:bg-white hover:text-[#0B132B] flex items-center justify-center transition-all duration-300 hover:scale-110"
                      aria-label="Get a quote"
                    >
                      <ArrowRight size={18} />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
              );
            })()}
          </div>
        </div>
      </section>

      <KeyNumbers product={product} />

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-12">
        <div className="lg:col-span-5">
          <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
            How it works
          </p>
          <h2
            data-reveal
            className="font-heading font-black text-4xl tracking-tighter leading-none text-[#0B132B]"
          >
            The plain-English version.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p
            data-reveal
            data-testid="product-how-it-works"
            className="text-lg text-[#334155] font-light leading-relaxed"
          >
            {product.howItWorks}
          </p>
        </div>
      </section>

      {/* Who it's for / Problems it solves */}
      <section className="bg-white border-y border-[#0B132B]/8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
          <div data-reveal className="rounded-3xl bg-[#EEF2F7] border border-[#0B132B]/5 p-7 md:p-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center">
                <Users size={20} />
              </div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold">
                Who it's for
              </p>
            </div>
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#0B132B] leading-tight">
              Built for the people it actually fits.
            </h2>
            <ul className="mt-7 md:mt-8 space-y-3">
              {product.whoFor.map((w, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-2xl bg-white px-5 py-4 border border-[#0B132B]/5"
                >
                  <Check size={18} className="text-[#29ABE2] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#334155] font-light leading-relaxed">{w}</span>
                </li>
              ))}
            </ul>
          </div>
          <div
            data-reveal
            data-reveal-delay="0.1"
            className="rounded-3xl bg-[#0B132B] text-white p-7 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#29ABE2]/20 blur-3xl pointer-events-none" />
            <div className="relative">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-2xl bg-white/10 ring-1 ring-white/15 text-[#29ABE2] flex items-center justify-center">
                  <AlertCircle size={20} />
                </div>
                <p className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold">
                  Problems it solves
                </p>
              </div>
              <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
                The pain points it was made for.
              </h2>
              <ul className="mt-7 md:mt-8 space-y-3">
                {product.problems.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-4"
                  >
                    <span className="w-2 h-2 rounded-full bg-[#29ABE2] mt-2 shrink-0" />
                    <span className="text-sm text-white/85 font-light leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-[#0B132B] text-white grain relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-28">
          <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
            Benefits at a glance
          </p>
          <h2
            data-reveal
            className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none max-w-2xl"
          >
            Why people choose {product.name}.
          </h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {(product.benefits || product.points).map((b, i) => (
              <div
                key={i}
                data-reveal
                data-reveal-delay={i * 0.06}
                className="rounded-3xl bg-white/5 border border-white/10 p-7 flex items-start gap-4 transition-colors duration-300 hover:border-[#29ABE2]/50"
              >
                <Check size={20} className="text-[#29ABE2] mt-1 shrink-0" />
                <p className="font-light leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <div data-reveal className="mt-14">
            <Link
              to={`/quote?product=${encodeURIComponent(product.name)}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#29ABE2] text-white font-semibold px-8 py-4 transition-all duration-300 hover:bg-white hover:text-[#0B132B] hover:scale-105"
            >
              Get my free {article} {product.tag} quote <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14 md:py-24">
        <p data-reveal className="text-sm tracking-[0.2em] uppercase text-[#29ABE2] font-semibold mb-4">
          Keep exploring
        </p>
        <h2
          data-reveal
          className="font-heading font-black text-3xl sm:text-4xl tracking-tighter text-[#0B132B] mb-10"
        >
          Other strategies worth knowing.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list
            .filter((p) => p.id !== product.id)
            .slice(0, 3)
            .map((p) => {
              const PIcon = p.icon;
              const to =
                source === "business" ? `/business/${p.id}` : `/products/${p.id}`;
              return (
                <Link
                  key={p.id}
                  to={to}
                  data-reveal
                  data-testid={`related-card-${p.id}`}
                  className="group rounded-3xl bg-white border border-[#0B132B]/5 shadow-sm p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#29ABE2]/30"
                >
                  <div className="w-11 h-11 rounded-2xl bg-[#29ABE2]/10 text-[#0a6fc2] flex items-center justify-center mb-5">
                    <PIcon size={20} />
                  </div>
                  <p className="text-xs tracking-[0.22em] uppercase text-[#29ABE2] font-semibold mb-2">
                    {p.tag}
                  </p>
                  <h3 className="font-heading font-bold text-xl text-[#0B132B]">{p.name}</h3>
                  <p className="mt-2 text-sm text-[#334155] font-light leading-relaxed">
                    {p.short || p.summary}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a6fc2]">
                    Learn more
                    <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
        </div>
      </section>
    </main>
  );
}
